<template>
    <div
        class="overflow-hidden rounded-xl border border-black/5 bg-white shadow-xs dark:border-white/10 dark:bg-white/[0.02]"
    >
        <!-- Header -->
        <div class="flex items-center justify-between gap-3 px-4 py-3">
            <div class="flex items-center gap-3">
                <span
                    class="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                >
                    Tem sinal de fraude? · Parecer IA
                </span>

                <span
                    v-if="verdict"
                    class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold"
                    :class="verdictBadgeClass"
                >
                    {{ verdictLabel }}
                    <span
                        v-if="confidence !== null"
                        class="font-medium opacity-80"
                    >
                        {{ confidence }}%
                    </span>
                </span>

                <span
                    v-else-if="streaming"
                    class="inline-flex items-center gap-2 text-xs text-indigo-500 dark:text-indigo-400"
                >
                    <Loader :size="12" />
                    <Shimmer as="span" class="text-xs">investigando...</Shimmer>
                </span>
            </div>

            <div class="flex items-center gap-3">
                <span
                    v-if="createdAt && !streaming"
                    class="text-[11px] text-gray-400 dark:text-gray-500"
                    :title="createdAt"
                >
                    {{ freshnessLabel }}
                </span>

                <button
                    v-if="messages.length > 0 && !streaming"
                    type="button"
                    class="text-[11px] font-medium text-gray-400 transition-colors hover:text-rose-500 focus:outline-none dark:text-gray-500 dark:hover:text-rose-400"
                    @click="clearConversation"
                >
                    Limpar conversa
                </button>
            </div>
        </div>

        <div
            v-if="error"
            class="border-t border-gray-100 px-4 py-3 text-sm text-red-600 dark:border-gray-700 dark:text-red-400"
        >
            {{ error }}
        </div>

        <div
            v-if="!enabled && !loadingLatest"
            class="border-t border-gray-100 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400"
        >
            Parecer de IA desabilitado neste ambiente.
        </div>

        <!-- Conversa -->
        <div
            v-if="enabled"
            class="border-t border-gray-100 dark:border-gray-700"
        >
            <Conversation
                v-if="thread.length > 0 || streaming"
                class="h-[440px]"
            >
                <ConversationContent class="flex flex-col gap-4 px-4 py-4">
                    <template
                        v-for="(message, index) in thread"
                        :key="`m-${index}`"
                    >
                        <!-- Analista -->
                        <div
                            v-if="message.role === 'user'"
                            class="flex justify-end"
                        >
                            <div
                                class="max-w-[85%] rounded-[14px_14px_4px_14px] bg-indigo-500 px-3.5 py-2 text-sm leading-relaxed text-white"
                            >
                                {{ message.text }}
                            </div>
                        </div>

                        <!-- Agente -->
                        <div v-else class="flex gap-2.5">
                            <span
                                class="mt-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-indigo-500/10 text-[10px] font-bold text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-300"
                            >
                                IA
                            </span>
                            <div class="flex min-w-0 flex-1 flex-col gap-2">
                                <ChainOfThought
                                    v-if="message.steps.length > 0"
                                    v-model="message.stepsOpen"
                                >
                                    <ChainOfThoughtHeader class="text-xs">
                                        {{ message.steps.length }}
                                        {{
                                            message.steps.length === 1
                                                ? "verificação executada"
                                                : "verificações executadas"
                                        }}
                                    </ChainOfThoughtHeader>
                                    <ChainOfThoughtContent>
                                        <ChainOfThoughtStep
                                            v-for="step in message.steps"
                                            :key="step.id"
                                            :label="step.label"
                                            :description="step.detail"
                                            status="complete"
                                        >
                                            <template #icon>
                                                <CheckCircle2Icon
                                                    class="size-4 text-emerald-500"
                                                    aria-hidden="true"
                                                />
                                            </template>
                                        </ChainOfThoughtStep>
                                    </ChainOfThoughtContent>
                                </ChainOfThought>

                                <message-blocks :blocks="message.blocks" />
                            </div>
                        </div>
                    </template>

                    <!-- Turno em andamento -->
                    <div v-if="streaming" class="flex gap-2.5">
                        <span
                            class="mt-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-indigo-500/10 text-[10px] font-bold text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-300"
                        >
                            IA
                        </span>
                        <div class="flex min-w-0 flex-1 flex-col gap-2">
                            <div
                                v-if="liveSteps.length === 0 && !liveText"
                                class="flex items-center gap-2 py-0.5"
                            >
                                <Loader :size="14" class="text-indigo-400" />
                                <Shimmer as="span" class="text-sm"
                                    >Analisando a pergunta...</Shimmer
                                >
                            </div>

                            <ChainOfThought
                                v-if="liveSteps.length > 0"
                                :model-value="true"
                            >
                                <ChainOfThoughtHeader class="text-xs">
                                    Investigação em andamento...
                                </ChainOfThoughtHeader>
                                <ChainOfThoughtContent>
                                    <ChainOfThoughtStep
                                        v-for="step in liveSteps"
                                        :key="step.id"
                                        :label="step.label"
                                        :description="step.detail"
                                        :status="
                                            step.done ? 'complete' : 'active'
                                        "
                                    >
                                        <template #icon>
                                            <CheckCircle2Icon
                                                v-if="step.done"
                                                class="size-4 text-emerald-500"
                                                aria-hidden="true"
                                            />
                                            <Loader
                                                v-else
                                                :size="14"
                                                class="text-indigo-400"
                                            />
                                        </template>
                                    </ChainOfThoughtStep>
                                </ChainOfThoughtContent>
                            </ChainOfThought>

                            <message-blocks
                                v-if="liveBlocks.length > 0"
                                :blocks="liveBlocks"
                            />
                            <span
                                v-if="liveText"
                                class="-mt-1 text-sm text-indigo-400"
                                >▍</span
                            >
                        </div>
                    </div>
                </ConversationContent>
            </Conversation>

            <!-- Estado vazio -->
            <div
                v-else-if="!loadingLatest"
                class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400"
            >
                Pergunte ao agente: ele cruza alertas do monitoramento, perfil
                da conta, velocity de recusas, transações, reembolsos e
                identidades para responder com dados.
            </div>

            <!-- Sugestões -->
            <div
                v-if="!streaming && !loadingLatest"
                class="border-t border-gray-100 px-4 py-2.5 dark:border-gray-700"
            >
                <Suggestions>
                    <Suggestion
                        v-for="suggestion in suggestions"
                        :key="suggestion"
                        :suggestion="suggestion"
                        class="border-indigo-200 text-xs text-indigo-600 hover:bg-indigo-500/10 dark:border-indigo-400/30 dark:text-indigo-300"
                        @click="sendMessage(suggestion)"
                    />
                </Suggestions>
            </div>

            <!-- Input -->
            <form
                class="flex items-center gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-700"
                @submit.prevent="submit"
            >
                <input
                    v-model="draft"
                    type="text"
                    class="text-input"
                    placeholder="Pergunte sobre este seller..."
                    :disabled="streaming"
                    maxlength="2000"
                />
                <button
                    type="submit"
                    class="shrink-0 rounded-md bg-indigo-600 px-3.5 py-2 text-xs font-medium text-white transition-colors hover:bg-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40"
                    :disabled="streaming || draft.trim() === ''"
                >
                    Enviar
                </button>
            </form>
        </div>
    </div>
</template>

<script>
import { defineComponent, h } from "vue";
import { CheckCircle2Icon } from "@lucide/vue";
import {
    ChainOfThought,
    ChainOfThoughtContent,
    ChainOfThoughtHeader,
    ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import {
    Conversation,
    ConversationContent,
} from "@/components/ai-elements/conversation";
import {
    Suggestion,
    Suggestions,
} from "@/components/ai-elements/suggestion";
import { Loader } from "@/components/ai-elements/loader";
import { Shimmer } from "@/components/ai-elements/shimmer";
import HighlightedText from "../../../views/monitoring/HighlightedText.vue";
import { getEcho } from "@/lib/echo";

const TOOL_LABELS = {
    ProducerMonitoringSignalsTool:
        "Consultando alertas e incidentes do monitoramento",
    ProducerProfileTool: "Levantando perfil e histórico da conta",
    ProducerCardVelocityTool: "Analisando velocity de recusas por BIN",
    ProducerTransactionBreakdownTool: "Abrindo o breakdown de transações",
    ProducerRefundSpeedTool: "Medindo velocidade de reembolso",
    ProducerIdentityStatsTool: "Cruzando identidades e fingerprints",
    ProducerDailyTimelineTool: "Reconstruindo a timeline diária",
};

/* Mesmos chips do vocabulário visual do monitoring (labels.js). */
const VERDICTS = {
    SIM: {
        label: "SIM",
        class: "bg-rose-500/10 text-rose-600 ring-1 ring-inset ring-rose-500/25 dark:text-rose-400",
    },
    SUSPEITO: {
        label: "SUSPEITO",
        class: "bg-amber-400/10 text-amber-600 ring-1 ring-inset ring-amber-400/30 dark:text-amber-400",
    },
    NAO: {
        label: "NÃO",
        class: "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/25 dark:text-emerald-400",
    },
};

/**
 * Parser leve do markdown do agente (headings ## e bullets) para renderizar
 * cada linha com o HighlightedText do monitoring, em vez de HTML via
 * markdown-it.
 */
function parseBlocks(text) {
    const body = (text ?? "")
        .replace(/^\s*VEREDITO:.*$/im, "")
        .replace(/^\s*CONFIANCA:.*$/im, "")
        .replace(/^\s*CONFIANÇA:.*$/im, "")
        .trim();

    const blocks = [];

    for (const rawLine of body.split("\n")) {
        const line = rawLine
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/__(.+?)__/g, "$1")
            .trim();

        if (line === "") {
            continue;
        }

        const heading = line.match(/^#{1,4}\s+(.*)$/);

        if (heading) {
            blocks.push({ type: "heading", text: heading[1] });
            continue;
        }

        const bullet = line.match(/^[-*]\s+(.*)$/);

        if (bullet) {
            const last = blocks[blocks.length - 1];

            if (last?.type === "list") {
                last.items.push(bullet[1]);
            } else {
                blocks.push({ type: "list", items: [bullet[1]] });
            }

            continue;
        }

        blocks.push({ type: "paragraph", text: line });
    }

    return blocks;
}

/* Render dos blocos de uma mensagem do agente com o tokenizer do monitoring. */
const MessageBlocks = defineComponent({
    name: "MessageBlocks",
    components: { HighlightedText },
    props: {
        blocks: { type: Array, required: true },
    },
    render() {
        return h(
            "div",
            { class: "flex flex-col gap-2" },
            this.blocks.map((block) => {
                if (block.type === "heading") {
                    return h(
                        "h4",
                        {
                            class: "mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 first:mt-0 dark:text-gray-400",
                        },
                        block.text,
                    );
                }

                if (block.type === "list") {
                    return h(
                        "ul",
                        { class: "flex flex-col gap-1.5" },
                        block.items.map((item) =>
                            h(
                                "li",
                                {
                                    class: "flex gap-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200",
                                },
                                [
                                    h("span", {
                                        class: "mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500",
                                    }),
                                    h(HighlightedText, { text: item }),
                                ],
                            ),
                        ),
                    );
                }

                return h(
                    "p",
                    {
                        class: "text-sm leading-relaxed text-gray-700 dark:text-gray-200",
                    },
                    [h(HighlightedText, { text: block.text })],
                );
            }),
        );
    },
});

export default {
    name: "SellerAiAudit",

    components: {
        ChainOfThought,
        ChainOfThoughtContent,
        ChainOfThoughtHeader,
        ChainOfThoughtStep,
        Conversation,
        ConversationContent,
        Suggestion,
        Suggestions,
        Loader,
        Shimmer,
        CheckCircle2Icon,
        MessageBlocks,
    },

    props: {
        user: {
            type: Object,
            required: true,
        },
    },

    emits: ["verdict"],

    data() {
        return {
            enabled: true,
            loadingLatest: true,
            streaming: false,
            messages: [],
            review: null,
            verdict: null,
            confidence: null,
            createdAt: null,
            fresh: false,
            liveSteps: [],
            liveText: "",
            error: null,
            draft: "",
            pollTimer: null,
            pollStartedAt: null,
            realtime: false,
            finishing: false,
        };
    },

    computed: {
        verdictLabel() {
            return VERDICTS[this.verdict]?.label ?? this.verdict;
        },

        verdictBadgeClass() {
            return (
                VERDICTS[this.verdict]?.class ??
                "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            );
        },

        freshnessLabel() {
            if (!this.createdAt) {
                return "";
            }

            const label = new Date(this.createdAt).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });

            return this.fresh ? label : `${label} · desatualizado`;
        },

        /**
         * Conversa a renderizar. Sem mensagens, o último parecer persistido
         * vira o primeiro turno do agente para dar contexto ao chat.
         */
        thread() {
            if (this.messages.length > 0) {
                return this.messages;
            }

            if (this.review?.report) {
                return [
                    {
                        role: "assistant",
                        text: this.review.report,
                        blocks: parseBlocks(this.review.report),
                        steps: (this.review.tool_steps ?? []).map(
                            (step, index) => ({
                                id: `saved-${index}`,
                                label: TOOL_LABELS[step.tool] ?? step.tool,
                                detail: stepDetail(step.arguments),
                                done: true,
                            }),
                        ),
                        stepsOpen: false,
                    },
                ];
            }

            return [];
        },

        liveBlocks() {
            return parseBlocks(this.liveText);
        },

        suggestions() {
            const items = [];

            if (!this.verdict || !this.fresh) {
                items.push("Gerar parecer completo");
            }

            items.push("Posso liberar o saque?", "Por que esse veredito?");

            return items;
        },
    },

    mounted() {
        this.fetchConversation();
        this.subscribe();
    },

    beforeUnmount() {
        this.stopPolling();
        this.unsubscribe();
    },

    methods: {
        userId() {
            return this.user?.id ?? this.$route.params.user_id;
        },

        mapMessages(raw) {
            return (raw ?? []).map((message, index) => ({
                role: message.role,
                text: message.text,
                blocks:
                    message.role === "assistant"
                        ? parseBlocks(message.text)
                        : [],
                steps: (message.tool_steps ?? []).map((step, stepIndex) => ({
                    id: `s-${index}-${stepIndex}`,
                    label: TOOL_LABELS[step.tool] ?? step.tool,
                    detail: stepDetail(step.arguments),
                    done: true,
                })),
                stepsOpen: false,
            }));
        },

        async fetchConversation() {
            this.loadingLatest = true;

            try {
                const [latest, chat] = await Promise.all([
                    axios.get(`/api/user/${this.userId()}/audit/ai-review`),
                    axios.get(
                        `/api/user/${this.userId()}/audit/ai-review/chat`,
                    ),
                ]);

                this.enabled = latest.data.enabled;
                this.review = latest.data.review;

                if (latest.data.review) {
                    this.verdict = latest.data.review.verdict;
                    this.confidence = latest.data.review.confidence;
                    this.createdAt = latest.data.review.created_at;
                    this.fresh = latest.data.review.fresh;
                    this.emitVerdict();
                }

                this.messages = this.mapMessages(chat.data.messages);

                if (chat.data.running) {
                    this.streaming = true;
                    this.startPolling();
                }
            } catch (error) {
                // Sem conversa ou parecer não é erro: o card oferece o input.
            } finally {
                this.loadingLatest = false;
            }
        },

        submit() {
            const message = this.draft.trim();

            if (message !== "") {
                this.sendMessage(message);
            }
        },

        async sendMessage(text) {
            this.error = null;
            this.draft = "";

            // O parecer salvo vira mensagem real ao iniciar a conversa.
            if (this.messages.length === 0 && this.thread.length > 0) {
                this.messages = this.thread;
            }

            this.messages = [
                ...this.messages,
                { role: "user", text, blocks: [], steps: [], stepsOpen: false },
            ];

            this.streaming = true;
            this.liveSteps = [];
            this.liveText = "";

            try {
                await axios.post(
                    `/api/user/${this.userId()}/audit/ai-review/chat`,
                    { message: text },
                );

                this.startPolling();
            } catch (error) {
                this.streaming = false;
                this.error =
                    error.response?.data?.message ??
                    "Falha ao enviar a mensagem.";
            }
        },

        async clearConversation() {
            try {
                await axios.delete(
                    `/api/user/${this.userId()}/audit/ai-review/chat`,
                );
                this.messages = [];
            } catch (error) {
                this.error = "Falha ao limpar a conversa.";
            }
        },

        /**
         * Stream em tempo real via Pusher: cada flush do job chega como
         * evento .progress com o mesmo shape do snapshot do polling. O
         * polling continua rodando como fallback, mais lento quando o
         * canal está de pé.
         */
        subscribe() {
            const echo = getEcho();

            if (echo === null) {
                return;
            }

            this.channel = echo
                .private(`seller-ai-review.${this.userId()}`)
                .listen(".progress", (snapshot) => {
                    this.realtime = true;

                    if (this.streaming) {
                        this.applyLiveSnapshot(snapshot);
                    }
                });
        },

        unsubscribe() {
            if (this.channel) {
                getEcho()?.leave(`seller-ai-review.${this.userId()}`);
                this.channel = null;
            }
        },

        async applyLiveSnapshot(snapshot) {
            if (snapshot.status === "done") {
                if (this.finishing) {
                    return;
                }

                // Busca a conversa final ANTES de derrubar o turno de
                // streaming: derrubar primeiro faz a resposta sumir e
                // reaparecer (flicker).
                this.finishing = true;
                this.stopPolling();

                try {
                    await this.fetchConversation();
                } finally {
                    this.streaming = false;
                    this.liveSteps = [];
                    this.liveText = "";
                    this.finishing = false;
                }

                return;
            }

            this.liveText = snapshot.text ?? "";
            this.liveSteps = (snapshot.steps ?? []).map((step) => ({
                id: step.id,
                label: TOOL_LABELS[step.tool] ?? step.tool,
                detail: stepDetail(step.arguments),
                done: step.done,
            }));

            if (snapshot.status === "failed") {
                this.stopPolling();
                this.streaming = false;
                this.liveSteps = [];
                this.liveText = "";
                this.error = snapshot.error ?? "Falha ao responder.";
            }
        },

        startPolling() {
            this.pollStartedAt = Date.now();
            this.stopPolling();
            this.pollTimer = setInterval(
                () => this.poll(),
                this.realtime ? 10000 : 2500,
            );
            this.poll();
        },

        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },

        async poll() {
            if (Date.now() - this.pollStartedAt > 6 * 60 * 1000) {
                this.stopPolling();
                this.streaming = false;
                this.error = "A análise demorou demais. Tente novamente.";
                return;
            }

            try {
                const { data } = await axios.get(
                    `/api/user/${this.userId()}/audit/ai-review/progress`,
                );

                if (data.status === "idle") {
                    return;
                }

                await this.applyLiveSnapshot(data);
            } catch (error) {
                // Erro transitório de rede: o próximo tick tenta de novo.
            }
        },

        emitVerdict() {
            this.$emit("verdict", {
                verdict: this.verdict,
                confidence: this.confidence,
                fresh: this.fresh,
            });
        },
    },
};

function stepDetail(args) {
    if (!args || typeof args !== "object") {
        return null;
    }

    if (args.bin) {
        return `BIN ${args.bin}`;
    }

    if (args.window_days) {
        return `${args.window_days}d`;
    }

    return null;
}
</script>
